import { useContext, useState } from "react";
import { Box, Typography, Link, Stack, Button, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { CourseLanguageOverrides, TargetMetadata } from "@backend-types";
import { ReactComponent as IconVideo } from "../../../assets/IconVideo.svg";
import { ReactComponent as IconVideoFill } from "../../../assets/IconVideoFill.svg";
import { ReactComponent as IconRepeat } from "../../../assets/IconRepeat.svg";
import { ReactComponent as IconCheck } from "../../../assets/IconCheck.svg";
import { ReactComponent as IconEllipsis } from "../../../assets/IconEllipsis.svg";
import { useHistory } from "react-router-dom";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { CourseStructureCtx, LockStatus, UniversalModalCtx } from "../../../interfaces";
import { HandleDataLayer } from "../../../Helpers";
import { Question, Target as TargetTree } from "../../utils/CourseTree";
import { UniversalModalContext } from "../../../context/UniversalModalContext";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  targetContainer: {
    flexGrow: 1,
    marginBottom: 48,
  },
  dashedBorder: {
    alignSelf: "stretch",
    flex: "0 0 3.5em",
    borderLeft: `2px dashed ${theme.palette.divider}`,
    // paddingLeft: 54,
  },
  statusIcon: {
    width: 28,
    height: 28,
    position: "absolute",
    left: -14,
    top: -4,
    color: "white",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statusInProgress: {
    backgroundColor: theme.palette.secondary.main,
  },
  statusNeedsPractice: {
    backgroundColor: theme.palette.warning.main,
  },
  statusMastered: {
    backgroundColor: theme.palette.success.main,
  },
  statusNotStarted: {
    backgroundColor: theme.palette.divider,
  },
  videoLink: {
    display: "flex",
    alignItems: "center",
    "& span": {
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
}));

export interface LearningTargetCardProps {
  target: TargetTree;
  lockStatus?: LockStatus;
  resumeQuestion: Question | undefined;
  last: boolean;
  openSkillVideoModal: () => void;
  status: string;
  cardVariant?: "student" | "teacher";
  languageOverrides?: CourseLanguageOverrides;
}

export function LearningTargetCard(props: LearningTargetCardProps) {
  const {
    lockStatus,
    target,
    resumeQuestion,
    last,
    status,
    cardVariant = "student",
    languageOverrides,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const [linkHover, setLinkHover] = useState(false);
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
  const universalModalCtx = useContext(UniversalModalContext) as UniversalModalCtx;
  const targetSkills = target.getAllTargetSkills();
  const stateOverrides = languageOverrides?.problem_states;
  const currentCourse = courseStructureCtx.courseTreeStructure?.getCurrentCourse();
  const currentTargetName = target.getFormattedTargetName();

  const getStatusText = () => {
    switch (status) {
      case "inprogress":
        return stateOverrides?.in_progress ?? "IN PROGRESS";
      case "needspractice":
        return stateOverrides?.needs_practice ?? "NEEDS MORE PRACTICE";
      case "mastered":
        if (target.getFirstMasteryProblem()) {
          return stateOverrides?.mastered ?? "MASTERY PROBLEM COMPLETED";
        } else {
          return stateOverrides?.mastered ?? "MASTERED";
        }
      default:
        return "NOT STARTED";
    }
  };

  const getButtonText = () => {
    if (cardVariant === "teacher") {
      return "View As Student";
    }
    switch (status) {
      case "inprogress":
        return "Continue";
      case "needspractice":
        return "Try again";
      case "mastered":
        return "Review";
      default:
        return "Start";
    }
  };

  const getStatusIcon = () => {
    if (cardVariant === "teacher") {
      return (
        <Box className={`${classes.statusIcon} ${classes.statusInProgress}`}>
          {/*<IconEllipsis style={{ width: "18px", height: "18px" }} />*/}
        </Box>
      );
    }
    switch (status) {
      case "inprogress":
        return (
          <Box className={`${classes.statusIcon} ${classes.statusInProgress}`}>
            <IconEllipsis style={{ width: "18px", height: "18px" }} />
          </Box>
        );
      case "needspractice":
        return (
          <Box className={`${classes.statusIcon} ${classes.statusNeedsPractice}`}>
            <IconRepeat style={{ width: "18px", height: "18px" }} />
          </Box>
        );
      case "mastered":
        return (
          <Box className={`${classes.statusIcon} ${classes.statusMastered}`}>
            <IconCheck style={{ width: "18px", height: "18px" }} />
          </Box>
        );
      default:
        return (
          <Box className={`${classes.statusIcon} ${classes.statusNotStarted}`}></Box>
        );
    }
  };

  const handleStart = () => {
    if (!resumeQuestion || lockStatus?.is_locked) return;
    if (cardVariant === "student") {
      HandleDataLayer(
        "link",
        "click",
        "internal link",
        "main content",
        `${currentTargetName}`,
        `${getButtonText()}`,
        "onclick",
        "learning targets"
      );
      courseStructureCtx.setBackBehavior({ backTo: "moduleoverview" });
    } else {
      HandleDataLayer(
        "link",
        "click",
        "internal link",
        "main content",
        `${currentTargetName}`,
        `${getButtonText()}`,
        "onclick",
        `${currentCourse?.name + " ^ module activities"}`.toLowerCase()
      );
      //module=8.EE.C.7.1&courseId=1&content=1
      courseStructureCtx.setBackBehavior({
        backTo: "deeplinking",
        route: "/explore/activities",
        search: new URLSearchParams({
          module: `${target?.module_id}`,
          courseId: `${courseStructureCtx.courseTreeStructure?.getCurrentCourse().id}`,
          content: "1",
        }).toString(),
      });
    }
    history.push({
      pathname: "/solver",
      search: "&questionId=" + resumeQuestion.id,
    });
  };

  const getTargetAzIdText = (target: TargetTree) => {
    const targetMetadata = target.metadata as TargetMetadata;
    const title =
      targetMetadata && "AZ_state_standard_code" in targetMetadata
        ? targetMetadata.AZ_state_standard_code
        : target.id.split(".").splice(0, 4).join(".");

    return (
      <Typography
        sx={{
          marginTop: "16px",
          font: "normal normal normal 16px/24px Roboto",
          color: "#747474",
          cursor: "pointer",
        }}>
        AZ Math:{" "}
        {
          <Tooltip title={currentTargetName} placement="bottom" arrow>
            <Typography
              onClick={() => {
                HandleDataLayer(
                  "modal",
                  "open",
                  "click",
                  "main content",
                  currentTargetName,
                  `${title}`,
                  "onclick",
                  `${currentCourse?.name + " ^ module activities"}`.toLowerCase()
                );
                if (targetMetadata && "AZ_state_standard" in targetMetadata) {
                  universalModalCtx.setSimpleModalContent({
                    title: `AZ Math: ${title}`,
                    subtitle: currentTargetName,
                    body:
                      targetMetadata.AZ_state_standard?.split("\n")
                        .map((item, index) => {
                          return `${item}`;
                        })
                        .join("\n") || "",
                  });
                  universalModalCtx.setSimpleContentModalOpen(true);
                }
              }}
              component={"span"}
              sx={{
                font: "normal normal normal 16px/24px Roboto",
                textDecoration: "underline",
                color: "#747474",
              }}>
              {title}
            </Typography>
          </Tooltip>
        }
      </Typography>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.dashedBorder} sx={last ? { maxHeight: "90px" } : undefined}>
        {getStatusIcon()}
      </Box>
      <Box className={classes.targetContainer}>
        <Stack direction="row" justifyContent="start" alignItems="flex-start">
          <Box sx={{ marginRight: "auto", flexGrow: 1 }}>
            {cardVariant === "student" && (
              <Typography sx={{ marginBottom: "16px" }} variant="body2">
                {getStatusText()}
              </Typography>
            )}
            <Typography variant="h3" component={"p"}>
              {currentTargetName}
            </Typography>
            {cardVariant === "teacher" &&
              !courseMetadata?.disable_AZ_math_standard &&
              getTargetAzIdText(target)}
          </Box>
          <Box sx={{ marginLeft: "24px" }}>
            <Button
              variant="outlined"
              size="large"
              sx={{ textWrap: "nowrap", padding: "1em", width: "auto" }}
              disabled={props.lockStatus?.is_locked}
              onClick={handleStart}>
              {getButtonText()}
            </Button>
          </Box>
        </Stack>
        <Box>
          <Typography sx={{ marginTop: "24px", marginBottom: "24px" }} variant="body2">
            {target.target_page_summary}
          </Typography>
          {targetSkills?.length > 0 && (
            <Link
              onMouseEnter={() => setLinkHover(true)}
              onMouseLeave={() => setLinkHover(false)}
              className={classes.videoLink}
              onClick={() => {
                if (cardVariant === "student") {
                  HandleDataLayer(
                    "modal",
                    "open",
                    "click",
                    "main content",
                    `${currentTargetName}`,
                    "related skill videos",
                    "onclick",
                    "learning targets"
                  );
                } else {
                  HandleDataLayer(
                    "modal",
                    "open",
                    "click",
                    "main content",
                    `${currentTargetName}`,
                    "Skill videos included in this target",
                    "onclick",
                    `${currentCourse?.name + " ^ module activities"}`.toLowerCase()
                  );
                }
                props.openSkillVideoModal();
              }}>
              {linkHover ? (
                <IconVideoFill style={{ width: 21, height: 21, marginRight: "0.5em" }} />
              ) : (
                <IconVideo style={{ width: 21, height: 21, marginRight: "0.5em" }} />
              )}
              <span>
                {cardVariant === "student"
                  ? "Related Skill Videos"
                  : "Skill videos included in this target"}
              </span>
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
}
